import * as React from "react";
import { useState, useEffect } from "react";
import { ProductTile } from "@src/stories/ecommerce/Product/ProductTile";
import { PromotionTile } from "@src/stories/ecommerce/PromotionalTile/PromotionTile";
import { CollectionSidebar } from "@src/stories/ecommerce/Collection/CollectionSidebar";
import { CollectionToolbar } from "@src/stories/ecommerce/Collection/CollectionToolbar";
import { LoadingSpinner } from "@arctheme-components/elements/LoadingSpinner/LoadingSpinner";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { getSearchProvider } from "@src/providers/Search";
import { useSearch } from "@src/contexts/SearchProvider";
import { SearchHeaderProvider, useSearchHeader } from "@src/contexts/SearchHeaderContext";
import { useSearchSettings } from "@src/contexts/SearchSettingsContext";
import { processSearchRequest, getCurrentPageFromUrl } from "@src/utilities/searchRequestHandler";

export const Search = ({ settings }) => {
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  
  // Get global search settings
  const { searchSettings, updateSearchSettings } = useSearchSettings();
  
  // Get products per page from settings
  const productsPerPage = settings.products_per_page || 24;
  
  // Get current page from URL
  const initialPage = getCurrentPageFromUrl();

  const toggleSidebar = () => {
    setSidebarVisible(!isSidebarVisible);
  };

  // Only render if settings.section and columns_mobile are defined
  if (!settings?.section || !settings.section.columns_mobile) {
    return (
      <></>
    );
  }

  const isMobile = useMobileBreakpoint(settings.mobile_breakpoint);

  const {
    columns_mobile,
    columns_desktop,
    column_gap_mobile,
    column_gap_desktop,
    row_gap_mobile,
    row_gap_desktop,
    id,
  } = settings.section;

  // Extract specific block settings
  const filterButtonSettings = settings.filter_button_settings;
  const sortDropdownSettings = settings.sort_dropdown_settings;
  const productTileSettings = settings.product_tile_settings;

  useEffect(() => {
    // Update global search settings if not yet initialized
    if (!searchSettings.initialized) {
      updateSearchSettings({
        search_system: settings.search_system || 'shopify',
        settings: settings
      });
    }

    // Process search request with proper pagination
    const searchParams = new URLSearchParams(window.location.search);
    const searchTerm = searchParams.get('q') || '';
    const searchDetail = processSearchRequest({
      type: "header",
      search: searchTerm,
      page: initialPage,
      productsPerPage: productsPerPage
    });

    // Dispatch search event
    window.dispatchEvent(
      new CustomEvent("searchrequest", {
        detail: searchDetail
      })
    );
  }, [searchSettings.initialized, updateSearchSettings, productsPerPage, initialPage]);

  // Determine which search provider to use - use global settings if available
  const providerName = searchSettings.initialized ? 
    searchSettings.search_system : 
    (settings.search_system || 'shopify');
    
  const SearchProviderComponent = getSearchProvider(providerName);
  // Proxy for provider props
  const SearchProviderProxy = (props) => <SearchProviderComponent {...props} />;

  return (
    <SearchHeaderProvider defaultSettings={{ productsPerPage }}>
      {(
        <SearchProviderProxy 
          settings={searchSettings.initialized ? searchSettings.settings : settings} 
          searchType="header-search"
        >
          <SearchWithProvider
            settings={settings}
            isMobile={isMobile}
            isSidebarVisible={isSidebarVisible}
            toggleSidebar={toggleSidebar}
            filterButtonSettings={filterButtonSettings}
            sortDropdownSettings={sortDropdownSettings}
            productTileSettings={productTileSettings}
            columns={{ 
              mobile: columns_mobile, 
              desktop: columns_desktop,
              gapMobile: column_gap_mobile,
              gapDesktop: column_gap_desktop,
              rowGapMobile: row_gap_mobile,
              rowGapDesktop: row_gap_desktop
            }}
            id={`${id}-search`}
            productsPerPage={productsPerPage}
            toolbarLayout={settings.section?.toolbar_layout || 'default'}
            productCountPosition={settings.section?.product_count_position || 'center'}
            sidebarPosition={settings.section?.sidebar_position || 'left'}
            showFilterIcon={settings.section?.show_filter_icon !== false}
            showSortLabel={settings.section?.show_sort_label || false}
            filterSortDirection={settings.section?.filter_sort_direction || 'normal'}
          />
        </SearchProviderProxy>
      )}
    </SearchHeaderProvider>
  );
};

// Internal component that uses the search provider context
function SearchWithProvider({ 
  settings, 
  isMobile, 
  isSidebarVisible, 
  toggleSidebar,
  filterButtonSettings,
  sortDropdownSettings,
  productTileSettings,
  columns,
  id,
  productsPerPage,
  toolbarLayout = 'default',
  productCountPosition = 'center',
  sidebarPosition = 'left',
  showFilterIcon = true,
  showSortLabel = false,
  filterSortDirection = 'normal'
}) {
  // Access the search provider context
  const search = useSearch();
  
  // Access collection context
  const { 
    products, 
    filters, 
    loading, 
    totalResults, 
    isLastPage,
    currentPage,
    loadMoreProducts 
  } = useSearchHeader();
  
  // Show appropriate title based on results
  const searchParams = new URLSearchParams(window.location?.search || '');
  const searchTerm = searchParams.get('q') || '';
  
  // Check if product tile features are enabled (using block settings with fallback to section settings)
  const enableWishlist = productTileSettings?.product_tile_wishlist_show || settings.section?.product_tile_wishlist_show || false;
  const showPrice = productTileSettings?.product_tile_price_show !== false && (settings.section?.product_tile_price_show !== false);
  const showQuickAdd = productTileSettings?.product_tile_quickadd_show || settings.section?.product_tile_quickadd_show || false;
  const showTags = productTileSettings?.product_tile_tag_show || settings.section?.product_tile_tag_show || false;

  // Calculate if this is the last page based on actual products loaded vs total results
  const isActuallyLastPage = totalResults <= products.length || products.length === 0;

  if (searchTerm === '') {  
    return (
      <></>
    );
  }

  return (
    <div data-search-provider={search.settings.search_system} data-current-page={currentPage}>
      <div className="search-header text-center mb-8 test">
        <h1 className="b4--bold">
          {loading && products.length === 0 ? 
            'Searching...' : 
            `${totalResults} results for "${searchTerm}"`}
        </h1>
      </div>

      <CollectionSidebar
        isVisible={isSidebarVisible}
        filters={filters}
        metaobject_filters={settings.filters}
        toggleSidebar={toggleSidebar}
        sidebarPosition={sidebarPosition}
      />
      
      <div className="collection__toolbar flex justify-between mb-4 items-center">
        <CollectionToolbar
          settings={settings}
          toggleSidebar={toggleSidebar}
          filterButtonSettings={filterButtonSettings}
          sortDropdownSettings={sortDropdownSettings}
          toolbarLayout={toolbarLayout}
          productCountPosition={productCountPosition}
          showFilterIcon={showFilterIcon}
          showSortLabel={showSortLabel}
          showActiveFiltersInToolbar={settings.show_active_filters_in_toolbar}
          totalResults={totalResults}
          filterSortDirection={filterSortDirection}
          resultsTextSettings={settings.results_text_settings}
        />
      </div>
      
      <div className="collection__container">
        <div
          id="product-grid"
          className={`collection__product-grid relative grid grid-cols-${columns.mobile} lg:grid-cols-${columns.desktop} gap-x-[${columns.gapMobile}px] lg:gap-x-[${columns.gapDesktop}px] gap-y-[${columns.rowGapMobile}px] lg:gap-y-[${columns.rowGapDesktop}px] auto-cols-max`}
          data-id={id}
        >
          {loading && products.length === 0 && (
            <div className="col-span-full flex justify-center items-center py-8">
              <LoadingSpinner id="spinner-search" />
            </div>
          )}

          {!loading && products.length === 0 && (
            <div className="col-span-full text-center py-8">
              <p className="text-lg mb-2">No products found</p>
              <p className="text-gray-500">Try adjusting your filters or search criteria</p>
            </div>
          )}

          {settings.promotion_blocks?.map((promotion, index) => (
            <div className="promotion-tile-wrapper" key={`promo-${index}`}>
              <PromotionTile promotion={promotion} settings={settings} />
            </div>
          ))}
          
          {products.map((product) => (
            <div key={`product-${product.id}`} className={"collection-tile"}>
              <ProductTile 
                settings={settings} 
                product={product} 
                siblings={undefined}
                globals={{}}
                enableWishlist={enableWishlist}
                showPrice={showPrice}
                showQuickAdd={showQuickAdd}
                showTags={showTags}
              />
            </div>
          ))}
          
          {loading && products.length > 0 && (
            <div className="col-span-full flex justify-center items-center py-8">
              <div className="w-8 h-8 border-4 border-gray-300 border-t-primary rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Pagination / Load More */}
        {products.length > 0 && !isActuallyLastPage && !loading && (
          <div className="collection__pagination flex justify-center mt-8">
            <button 
              onClick={loadMoreProducts}
              disabled={loading}
              className="button button-primary flex items-center"
              aria-label={`Load page ${currentPage + 1}`}
            >
              Load More
            </button>
          </div>
        )}

        {/* Product Count */}
        {products.length > 0 && (
          <div className="collection__product-count text-center text-gray-500 mt-4">
            Showing {products.length} of {totalResults} products
            {currentPage > 1 && (
              <span className="ml-2">(Page {currentPage})</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Search;
